import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AuthRules, Permission, SessionPayload } from '@wr/shared';
import { ApiError, get, setCsrfToken } from './api.js';
import { forgetSession, rememberSession, savedSession } from './offline/local.js';

/** offline: the app runs on the session kept on this device (no network or server down). */
type Loaded = { session: SessionPayload; rules: AuthRules; offline: boolean };

/**
 * null while loading; 'offline' when the API cannot be reached and nobody is
 * signed in on this device; 'offline_expired' when the session kept on the
 * device has ended (the person signs in again once online).
 */
type Status = Loaded | null | 'offline' | 'offline_expired';

interface SessionContextValue {
  status: Status;
  setSession(session: SessionPayload): void;
  refresh(): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/** No answer, or the server is down: the field keeps working (brief section 9). */
function unreachable(error: unknown): boolean {
  return error instanceof ApiError && (error.code === 'offline' || error.status >= 500);
}

/** How often the app tries the server again while it works offline. */
const RETRY_MS = 30_000;

async function load(): Promise<Exclude<Status, null>> {
  try {
    const [session, rules] = await Promise.all([
      get<SessionPayload>('/auth/session'),
      get<AuthRules>('/auth/rules'),
    ]);
    setCsrfToken(session.state === 'signed_out' ? undefined : session.csrfToken);
    return { session, rules, offline: false };
  } catch (error) {
    if (!unreachable(error)) throw error;
    const saved = await savedSession();
    if (saved === 'expired') return 'offline_expired';
    if (!saved) return 'offline';
    setCsrfToken(saved.session.csrfToken);
    return { ...saved, offline: true };
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>(null);

  const refresh = useCallback(async () => {
    setStatus(await load());
  }, []);

  useEffect(() => {
    let current = true;
    void load().then((loaded) => current && setStatus(loaded));
    return () => {
      current = false;
    };
  }, []);

  // Signed in: the session is kept on this device, so the app opens offline
  // until it ends. Signed out: it no longer opens offline (data stays).
  useEffect(() => {
    if (!status || typeof status === 'string' || status.offline) return;
    if (status.session.state === 'signed_in') void rememberSession(status.session, status.rules);
    if (status.session.state === 'signed_out') void forgetSession();
  }, [status]);

  // Offline: back to the server as soon as the network returns.
  const offline = status === 'offline' || status === 'offline_expired' || !!status?.offline;
  useEffect(() => {
    if (!offline) return;
    const retry = () => void refresh();
    window.addEventListener('online', retry);
    const timer = setInterval(retry, RETRY_MS);
    return () => {
      window.removeEventListener('online', retry);
      clearInterval(timer);
    };
  }, [offline, refresh]);

  const setSession = useCallback((session: SessionPayload) => {
    setCsrfToken(session.state === 'signed_out' ? undefined : session.csrfToken);
    setStatus((current) =>
      current && typeof current !== 'string' ? { ...current, session, offline: false } : current,
    );
  }, []);

  return (
    <SessionContext.Provider value={{ status, setSession, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside SessionProvider');
  return value;
}

/** The loaded session and rules; only inside screens rendered after loading. */
export function useLoaded(): Loaded & Omit<SessionContextValue, 'status'> {
  const { status, ...rest } = useSession();
  if (!status || typeof status === 'string') throw new Error('Session not loaded');
  return { ...status, ...rest };
}

/** The signed-in user and what they may do. */
export function useSignedIn() {
  const { session, ...rest } = useLoaded();
  if (session.state !== 'signed_in') throw new Error('Not signed in');
  const can = (permission: Permission) => session.permissions.includes(permission);
  return { ...session, can, ...rest };
}
